import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { RefObject, useEffect, useState } from "react";
import { css } from "styled-components";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import EditIcon from "@WhiteCap/Icons/Edit";
import DataTable from "@insite/mobius/DataTable/DataTable";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Link from "@insite/mobius/Link/Link";
import useRequestForQuotesForm from "@WhiteCap/Hooks/useRequestForQuotesForm";
import GuestRFQApiHandler, { GTMCartItem } from "@WhiteCap/Handlers/GuestRFQ/GuestRFQFormHandler";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import { connect, ResolveThunks } from "react-redux";
import getColor from "@insite/mobius/utilities/getColor";
import {
    isGuestUserAddressComplete,
    isGuestUserFormsCompleted,
} from "@WhiteCap/Widgets/GuestRFQ/CustomerBillingAndShippingInfo";
import { isGuestUserBasicInfoCompleted } from "@WhiteCap/Widgets/GuestRFQ/CustomerBasicInfo";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import ReCAPTCHA from "react-google-recaptcha";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";

{
    /* EC-235 : SubTask - EC-530 = Wizard Structure of the form (Level 2) i.e Customer Form Preview */
}

// Map state to props
const mapStateToProps = (state: CustomApplicationState) => ({
    isLoading: state.WhiteCap.GuestRFQForm,
    isAuthenticated: state.context.session.isAuthenticated,
    countries: getCurrentCountries(state),
    currentUrl: getLocation(state),
});

// Map dispatch to props
const mapDispatchToProps = {
    GuestRFQApiHandler,
    loadCurrentCountries,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
};

// Define the component's props type
type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

// Define styles for the form and its elements
export interface QouteCartFormStyles {
    formHeading?: InjectableCss;
    formContainer?: InjectableCss;
    formInner?: InjectableCss;
    btnWrapper?: InjectableCss;
    selectProvince?: InjectableCss;
    countrySelction?: InjectableCss;
    provinceWrapper?: InjectableCss;
    detailItem?: InjectableCss;
    editIconWrapper?: InjectableCss;
    previewHead?: InjectableCss;
    previewTable?: InjectableCss;
    tableWrapper?: InjectableCss;
    previousBtnStyles?: ButtonPresentationProps;
    proceedBtnStyles?: ButtonPresentationProps;
    countryName?: InjectableCss;
    recaptchaWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

// Define default styles
export const qouteCartFormStyles: QouteCartFormStyles = {
    formContainer: {
        css: css`
            border: 1px solid ${getColor("secondary")};
            margin-top: 30px;
        `,
    },
    formHeading: {
        css: css`
            padding: 15px 20px;
            background: ${getColor("secondary")};
            border-bottom: 1px solid ${getColor("secondary")};
            display: flex;
            align-items: center;
            justify-content: space-between;
            h3 {
                color: ${getColor("text.main")};
                font-size: 18px;
                font-style: normal;
                font-weight: 700;
                font-size: 18px;
                line-height: 26px;
            }
        `,
    },
    formInner: {
        css: css`
            padding: 20px 30px 20px;
        `,
    },
    btnWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-top: 30px;
        `,
    },
    selectProvince: {
        css: css`
            background: ${getColor("primary.contrast")} !important;
            border: 1px solid ${getColor("secondary")} !important;
            border-radius: 1px !important;
            font-size: 14px !important;
            line-height: 1.42857143 !important;
            font-weight: 400 !important;
            border-width: 1px !important;
            height: 38px !important;
            &:focus {
                outline: none !important;
            }
        `,
    },
    provinceWrapper: {
        css: css`
            span[class^="IconWrapper"] {
                background: ${getColor("primary.contrast")};
                height: 35px !important;
                svg {
                    color: ${getColor("text.main")};
                    background: ${getColor("primary.contrast")};
                }
            }
        `,
    },
    countrySelction: {
        css: css`
            border: 1px solid ${getColor("secondary")} !important;
            background-color: ${getColor("secondary")} !important;
        `,
    },
    detailItem: {
        css: css`
            flex-direction: column;
            label {
                font-size: 14px;
                font-weight: 700;
                line-height: 22px;
                letter-spacing: 0em;
                color: ${getColor("text.main")};
                margin-bottom: 7px;
            }
            span {
                font-size: 15px;
                font-weight: 400;
                line-height: 24px;
                text-align: left;
                color: ${getColor("text.main")};
            }
        `,
    },
    editIconWrapper: {
        css: css`
            span {
                display: flex;
                align-items: center;
            }
            display: flex;
            align-items: center;
            svg {
                margin-right: 5px;
            }
        `,
    },
    previewHead: {
        css: css`
            text-align: center;
            display: block;
            font-size: 16px;
            font-weight: 700;
            margin: 30px 0 30px 0;
        `,
    },
    previewTable: {
        css: css`
            border: 1px solid ${getColor("secondary")};
            thead {
                tr {
                    th {
                        background-color: ${getColor("secondary")};
                        border-bottom: 2px solid ${getColor("secondary")};
                    }
                }
            }
        `,
    },
    tableWrapper: {
        css: css`
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                            overflow-x: auto;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    countryName: {
        css: css`
            text-transform: uppercase;
        `,
    },
    recaptchaWrapper: {
        css: css`
            margin-top: 30px;
            display: flex;
            justify-content: flex-end;
        `,
    },
    spinner: {
        size: 22,
        css: css`
            margin: 8px 50px 0 34px;
        `,
    },
};

const styles = qouteCartFormStyles;
const StyledForm = getStyledWrapper("form");

const GuestUserFormPreview = ({
    history,
    GuestRFQApiHandler,
    isAuthenticated,
    isLoading,
    countries,
    loadCurrentCountries,
    validateReCaptcha,
    currentUrl,
}: Props) => {
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [gtmCart, setGtmCart] = useState<GTMCartItem[]>([]);


    useEffect(() => {
        // show an alert message when the user tries to close the browser
        window.addEventListener("beforeunload", alertUser);
        return () => {
            window.removeEventListener("beforeunload", alertUser);
        };
    }, []);
    const alertUser = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = "";
    };

    useEffect(() => {
        if (!countries || countries.length === 0) {
            loadCurrentCountries();
        }
    }, [loadCurrentCountries]);

    const canada = countries?.find(country => country.name === "Canada" || country.abbreviation === "CA");
    const states = canada ? canada.states || [] : [];

    const getProvinceNameById = (id: string) => {
        const state = states.find(state => state.id === id);
        return state ? state.name : "Unknown Province"; // Default if not found
    };

    const guestQuoteCart = JSON.parse(localStorage.getItem("rfqCart") || "{}");
    const { formData } = useRequestForQuotesForm();

    const redirectToPreviousState = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        history.push("/guest-user-billing-info");
    };

    const guestUserAddedProduct = localStorage.getItem("rfqCart");

    // Redirect user based on form completion and authentication status
    useEffect(() => {
        const cart = guestUserAddedProduct ? JSON.parse(guestUserAddedProduct) : null;
        const isCartEmpty = !cart || cart.quoteCount <= 0;

        if (currentUrl.pathname.includes("/Content/Page")) {
            // Don't do anything, just display the page for content admin
            return;
        }

        if (!isGuestUserFormsCompleted() && !isAuthenticated) {
            if (!isGuestUserBasicInfoCompleted()) {
                history.push("/guest-user-form");
            } else if (!isGuestUserAddressComplete() && !isAuthenticated) {
                history.push("/guest-user-billing-info");
            } else if (isAuthenticated) {
                history.push("/");
            }
        }

        if (isAuthenticated && !isCartEmpty) {
            // If user is authenticated and cart is not empty and clear local storage
            localStorage.removeItem("rfqCart");
            sessionStorage.removeItem("guestRfqFormData");
            history.push("/");
        }
    }, [history, guestUserAddedProduct, isAuthenticated]);

    // Handle form submission
    const SubmitHandler = async () => {
        setIsSubmitting(true);
        const isReCaptchaValid = await validateReCaptcha({ location: "GuestRFQ", recaptchaRef });

        if (!isReCaptchaValid) {
            setIsSubmitting(false);
            return;
        }
        const updatedFormData = { ...formData };

        if (canada) {
            updatedFormData.shippingCountry = canada.id;
            updatedFormData.billingCountry = canada.id;
        }

        // Populate gtmCart from localStorage
        const guestUserAddedProduct = localStorage.getItem("rfqCart");
        let cartItems: GTMCartItem[] = [];

        if (guestUserAddedProduct) {
            const parsedCart = JSON.parse(guestUserAddedProduct);

            // Convert parsedCart items to GTMCartItem array
            cartItems = parsedCart.products?.map((item: any) => ({
                ProductID: item.productNumber,
                Title: item.productTitle,
                Brand: item.brand?.name,
                Quantity: item.qty,
            })) || [];
        }

        setGtmCart(cartItems);

        const formSubmitted = true;

        if (formSubmitted) {
            GuestRFQApiHandler({
                ...updatedFormData,
                gtmCart: cartItems,
                onSuccess(quoteNumber: any) {
                    // Url Change for visiblity
                    setIsSubmitting(false);
                    sessionStorage.setItem("formSubmitted", "true");
                    history.push(`/quote-confirmation?GuestCartId=${quoteNumber}`);
                },
                
            });
        }
    };

    return (
        <StyledWrapper>
            {/* Preview Quote Products Section */}
            <StyledWrapper>
                <StyledWrapper>
                    <Typography {...styles.previewHead} as="span">
                        {translate("This is a preview of your quote. Confirm Quote to submit.")}
                    </Typography>
                    <StyledWrapper {...styles.tableWrapper}>
                        <DataTable {...styles.previewTable}>
                            <DataTableHead>
                                <DataTableHeader>{translate("Product No.")}</DataTableHeader>
                                <DataTableHeader>{translate("Description")}</DataTableHeader>
                                <DataTableHeader>{translate("UOM")}</DataTableHeader>
                                <DataTableHeader>{translate("Qty")}</DataTableHeader>
                            </DataTableHead>
                            <DataTableBody>
                                {guestQuoteCart.products &&
                                    guestQuoteCart.products.map((product: any) => (
                                        <DataTableRow>
                                            <DataTableCell>{product.productNumber}</DataTableCell>
                                            <DataTableCell>{product.productTitle}</DataTableCell>
                                            <DataTableCell>{product.unitOfMeasures?.find((uom: any) => uom.isDefault)?.unitOfMeasureDisplay || "N/A"}</DataTableCell>
                                            <DataTableCell>{product.qty}</DataTableCell>
                                        </DataTableRow>
                                    ))}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>

            {/* Preview Quote Customer Information Section */}
            <StyledWrapper {...styles.formContainer}>
                <StyledWrapper {...styles.formHeading}>
                    <Typography as="h3">{translate("Customer Information")}</Typography>
                    <Link href="/guest-user-form" {...styles.editIconWrapper}>
                        <EditIcon />
                        <Typography as="h3">{translate("Edit")}</Typography>
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.formInner}>
                    <GridContainer>
                        <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("Name")}</Typography>
                            <Typography as="span">{formData.customerName}</Typography>
                        </GridItem>
                        <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("Company")}</Typography>
                            <Typography as="span">{formData.companyName}</Typography>
                        </GridItem>
                        <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("Email")}</Typography>
                            <Typography as="span">{formData.emailAddress}</Typography>
                        </GridItem>
                        <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("Phone Number")}</Typography>
                            <Typography as="span">{formData.phoneNumber}</Typography>
                        </GridItem>
                        <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("Customer Number")}</Typography>
                            <Typography as="span">{formData.customerNumber}</Typography>
                        </GridItem>
                         {/* EC-1018 Remove Customer PO from-Guest-Quote form */}
                        {/* <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("Customer PO")}</Typography>
                            <Typography as="span">{formData.customerPo}</Typography>
                        </GridItem> */}
                        <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("Customer Job/Tag Number")}</Typography>
                            <Typography as="span">{formData.customerJob}</Typography>
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>

            {/* EC-898 [FE] Add notes field to Guest Quote Form */}
            {/* Order note section  */}
            <StyledWrapper {...styles.formContainer}>
                <StyledWrapper {...styles.formHeading}>
                    <Typography as="h3">{translate("Order Notes")}</Typography>
                    <Link href="/guest-user-form" {...styles.editIconWrapper}>
                        <EditIcon />
                        <Typography as="h3">{translate("Edit")}</Typography>
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.formInner}>
                    <GridContainer>
                        {/* Make a full length order note text using grid width */}
                        <GridItem width={[12, 12, 12, 12, 12]} {...styles.detailItem}>
                            <Typography as="label">{translate("Notes")}</Typography>
                            <Typography as="span">{formData.orderNotes}</Typography>
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...styles.formContainer}>
                <StyledWrapper {...styles.formHeading}>
                    <Typography as="h3">{translate("Also Sent To")}</Typography>
                    <Link href="/guest-user-form" {...styles.editIconWrapper}>
                        <EditIcon />
                        <Typography as="h3">{translate("Edit")}</Typography>
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.formInner}>
                    <GridContainer>
                        <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("Recipients")}</Typography>
                            {formData.sendCopyToEmail.split(",").map((email, index) => (
                                <Typography as="span" key={index} style={{ display: "block" }}>
                                    {email.trim()}
                                </Typography>
                            ))}
                        </GridItem>
                        {/* Make a full length message sent text */}
                        <GridItem width={[12, 12, 8, 8, 8]} {...styles.detailItem}>
                            <Typography as="label">{translate("Message Sent")}</Typography>
                            <Typography as="span">{formData.sendCopyToComment}</Typography>
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...styles.formContainer}>
                <StyledWrapper {...styles.formHeading}>
                    <Typography as="h3">{translate("Billing Information")}</Typography>
                    <Link href="/guest-user-billing-info" {...styles.editIconWrapper}>
                        <EditIcon />
                        <Typography as="h3">{translate("Edit")}</Typography>
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.formInner}>
                    <GridContainer>
                        <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("Billing Name")}</Typography>
                            <Typography as="span">{formData.billingName}</Typography>
                        </GridItem>
                        <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("Address")}</Typography>
                            <Typography as="span">{formData.billingAddress}</Typography>
                        </GridItem>
                        <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("City")}</Typography>
                            <Typography as="span">{formData.billingCity}</Typography>
                        </GridItem>
                        <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("Province")}</Typography>
                            <Typography as="span">{getProvinceNameById(formData.billingProvince)}</Typography>
                        </GridItem>
                        <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("Postal Code")}</Typography>
                            <Typography as="span">{formData.billingPostalCode}</Typography>
                        </GridItem>
                        <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("Country")}</Typography>
                            <Typography {...styles.countryName} as="span">
                                {canada?.name}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...styles.formContainer}>
                <StyledWrapper {...styles.formHeading}>
                    <Typography as="h3">{translate("Shipping  Information")}</Typography>
                    <Link href="/guest-user-billing-info" {...styles.editIconWrapper}>
                        <EditIcon />
                        <Typography as="h3">{translate("Edit")}</Typography>
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.formInner}>
                    <GridContainer>
                        <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("Recipient Name")}</Typography>
                            <Typography as="span">{formData.shippingRecipientName}</Typography>
                        </GridItem>
                        <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("Address")}</Typography>
                            <Typography as="span">{formData.shippingAddress}</Typography>
                        </GridItem>
                        <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("City")}</Typography>
                            <Typography as="span">{formData.shippingCity}</Typography>
                        </GridItem>
                        <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("Province")}</Typography>
                            <Typography as="span"> {getProvinceNameById(formData.shippingProvince)}</Typography>
                        </GridItem>
                        <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("Postal Code")}</Typography>
                            <Typography as="span">{formData.shippingPostalCode}</Typography>
                        </GridItem>
                        <GridItem width={[12, 12, 4, 4, 4]} {...styles.detailItem}>
                            <Typography as="label">{translate("Country")}</Typography>
                            <Typography {...styles.countryName} as="span">
                                {canada?.name}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...styles.recaptchaWrapper}>
                <ReCaptcha recaptchaRef={recaptchaRef} location="GuestRFQ" />
            </StyledWrapper>

            <StyledWrapper {...styles.btnWrapper}>
                <Button variant="secondary" onClick={redirectToPreviousState}>
                    {translate("Previous")}
                </Button>
                {!isSubmitting && 
                    <Button {...styles.proceedBtnStyles} onClick={SubmitHandler} disabled={isSubmitting}>
                        {translate("Confirm Quote")}
                    </Button>
                }
                {isSubmitting && <LoadingSpinner {...styles.spinner} />}

            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(GuestUserFormPreview)),
    definition: {
        displayName: "Guest User Form Preview",
        group: "Common",
    },
};

export default widgetModule;

{
    /* EC-235 : SubTask - EC-530 = Wizard Structure of the form (Level 2) i.e Customer Form Preview */
}
